import { useEffect, useRef } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { canvasRefAtom, selectedItemsAtom, showPinturaAtom } from 'atoms'
import { multiSelectModeAtom } from '@/atoms/_editorAtoms'
import { EDITOR_EDIT_CHROME_SELECTOR } from '@/utils/editor-text-box/editorTextBoxAdapter'

const CLICK_JITTER_TOLERANCE_PX = 3

/**
 * The editor's panels by their `data-editor-ui` value. A click on the left
 * sidebar (its tabs, categories and slideout) or on the bottom toolbar blurs
 * a merely selected Lotus box; the right panel holds the selected item's own
 * settings and keeps it, and so does the bulk-processing overlay. A box being
 * edited keeps its session through every panel: the Case tab authors the
 * values the open box shows.
 */
const PANELS_BLURRING_SELECTION = new Set(['insert-menu', 'editor-toolbar'])

const EDITING_TEXT_BOX_SELECTOR = '[data-editor-text-editing]'

/**
 * `tolerateClickJitter`: an editor text box or rule counts a press as a drag
 * only past CLICK_JITTER_TOLERANCE_PX of travel, so a slightly unsteady
 * click still deselects; a classic (TinyMCE-era) item keeps the staging
 * rule, where any mousemove between press and release makes the click a
 * drag.
 */
export const useEditorOutsideClick = (
  ref,
  containerRef,
  active,
  type = 'click',
  { panelsBlurSelection = false, tolerateClickJitter = false } = {},
) => {
  const canvasRef = useRecoilValue(canvasRefAtom)
  const setSelectedItems = useSetRecoilState(selectedItemsAtom)
  const multiSelectMode = useRecoilValue(multiSelectModeAtom)
  const showPintura = useRecoilValue(showPinturaAtom)

  const isDragging = useRef(false)
  const pressOrigin = useRef(null)

  const handleMouseDown = (e) => {
    pressOrigin.current = { x: e.clientX, y: e.clientY }
    isDragging.current = false
  }

  const handleMouseMove = (e) => {
    if (!tolerateClickJitter) {
      isDragging.current = true
      return
    }
    if (!pressOrigin.current || isDragging.current) return
    const dx = e.clientX - pressOrigin.current.x
    const dy = e.clientY - pressOrigin.current.y
    if (dx * dx + dy * dy > CLICK_JITTER_TOLERANCE_PX ** 2)
      isDragging.current = true
  }

  const handleClick = (e) => {
    if (isDragging.current) return

    if (multiSelectMode) return

    if (showPintura) return

    // The canvas itself carries data-editor-ui (it hosts the text toolbar's
    // portal), so it has to fall through: counting it as a UI panel would make
    // every in-canvas click a no-op and nothing could ever be deselected.
    const panel = e.target
      ?.closest?.('[data-editor-ui]')
      ?.getAttribute('data-editor-ui')
    if (panel && panel !== 'editor-canvas') {
      if (
        active &&
        panelsBlurSelection &&
        PANELS_BLURRING_SELECTION.has(panel) &&
        !containerRef?.current?.querySelector(EDITING_TEXT_BOX_SELECTOR)
      )
        setSelectedItems([])
      return
    }

    if (
      e.target?.closest?.(
        `[data-editor-text-toolbar-strip], [data-editor-text-popover], [data-editor-text-sizing-inspector], ${EDITOR_EDIT_CHROME_SELECTOR}`,
      )
    )
      return

    // Don't deselect when clicking on the item control overlay
    if (e.target?.closest?.('[data-item-control-overlay]')) return

    if (active && canvasRef && !canvasRef.contains(e.target)) {
      setSelectedItems([])
      return
    }

    if (
      active &&
      ref.current &&
      canvasRef &&
      !ref.current.contains(e.target) &&
      canvasRef.contains(e.target) &&
      (!containerRef || !containerRef.current.contains(e.target))
    ) {
      const clickedOnSelectedItem = e.target.closest(
        '[data-item-selected="true"]',
      )
      if (clickedOnSelectedItem) {
        return
      }

      setSelectedItems([])
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener(type, handleClick)

    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener(type, handleClick)
    }
  })
}
